import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetInventories } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { IInventory, Inventory } from '../../../@types/model/masterData/inventory/inventory';
import { IInventoryFormValues, InventoryFormValues } from '../../../@types/model/masterData/inventory/inventoryFormValues';
import InventoryForm from './form/InventoryForm';
import InventoryHttpService from '../../../services/http/masterData/inventoryHttpService';
import { useLocation } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { IApiError } from '../../../@types/other';
import { setInventoryMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';

const InventoryScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const inventories = useAppSelector(x => x.masterData.inventories);
    const auth = useAppSelector(x => x.auth);
    const pathname = useLocation().pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedInventory, setSelectedInventory] = useState<IInventory>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingInventory, setDeletingInventory] = useState<IInventory>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await InventoryHttpService.getInventoryData();
                    dispatch(dataSetInventories(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading inventories.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = useCallback(() => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setSelectedInventory(undefined);
        setFormGuid(undefined);
    }, []);

    const editInventory = useCallback((inventory : IInventory) => {
        setSelectedInventory(inventory);
        setFormGuid(inventory.guid);
        setIsDialogOpen(true);
    }, []);

    const onSubmit = async (values : IInventoryFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        if (isNullOrWhiteSpace(payload.guid)) {
            payload.guid = formGuid ?? '';
        }

        try {
            const res = await InventoryHttpService.addOrUpdateInventory(Inventory(payload));
            const updatedList = upsertArrayElement(inventories, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetInventories(updatedList));
            await setInventoryMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedInventory ? 'Inventory updated successfully.' : 'Inventory added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating inventory data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = useCallback((inventory : IInventory) => {
        setDeletingInventory(inventory);
        setIsDeletePopupOpen(true);
    }, []);

    const closeDeleteConfirmationPopup = useCallback(() => {
        setDeletingInventory(undefined);
        setIsDeletePopupOpen(false);
    }, []);

    const removeInventory = async () => {
        if (!deletingInventory) return;
        const updatedInventory = { ...deletingInventory, isActive: false };
        setIsLoading(true);
        try {
            const res = await InventoryHttpService.deleteInventory(updatedInventory.id);
            const updatedList = upsertArrayElement(inventories, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetInventories(updatedList));
            await setInventoryMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Inventory deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting inventory.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return InventoryFormValues(selectedInventory);
        }
    }, [selectedInventory, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => pathname.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Inventory'
                    isEdit={!!selectedInventory}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <InventoryForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IInventory>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editInventory}
                        enableEditing={hasEditingRight}
                        enableDeleting={inventory => inventory.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        fitWidthToPage
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Description', field: 'description', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={inventories}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Inventory'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this inventory?'
                onAccept={removeInventory}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default InventoryScreen;
